/**
 * Gold Diggers Performance Pool — Canonical Constants  v1.1.0 (2026-04-19)
 *
 * Shared scoring / payout constants. Consumers as of v4.3.72:
 *   - api/cron/process-performance-pool.js   (daily payout writer —
 *     now reads daily_team_volume_summary; uses PAYOUT_DISTRIBUTION + RANK_SPLITS)
 *   - api/teams/leaderboard.js               (live UI leaderboard —
 *     now reads daily_team_volume_summary; imports no scoring constants)
 *   - api/teams/compute-historical.js        (admin-triggered historical
 *     backfill — STILL uses the deprecated weighted model; LEVEL_WEIGHTS
 *     is retained below as a deprecated export solely for this consumer)
 *
 * SCORING MODEL AS OF v4.3.72:
 *   Unweighted Total Sales Volume (USD). Teams are ranked by the sum of
 *   purchase USD credited to them via the credit_teams_for_purchase trigger
 *   (database/migrations/gold-diggers-volume-scoring.sql). Primary key is
 *   total_volume_usd DESC with earliest_purchase_timestamp ASC as tie-break.
 *   LEVEL_WEIGHTS is NOT used by the live scoring path.
 *
 * Historical context (2026-04-18 remediation):
 * Prior to this module, each of the three files defined its own scoring
 * constants. The lists agreed on 6 items but DIVERGED on 'Commission Shares'
 * — compute-historical excluded it, cron and leaderboard did not. That drift
 * was a contributing factor in the 2026-04-16 / 04-17 duplicate-winner
 * conflict (see header of api/cron/process-performance-pool.js D1 audit).
 *
 * DO NOT silently add or remove items from EXCLUDED_METHODS in this file.
 * The canonical list is DUAL-HOMED (see EXCLUDED_METHODS comment below).
 * Any change here propagates to every consumer and materially affects
 * real share-ownership competition outcomes.
 */

// DEPRECATED (v4.3.72) — retained for api/teams/compute-historical.js only.
// The live scoring path (leaderboard + cron) now uses an unweighted Total
// Sales Volume model and does not reference this array. Remove this export
// only after compute-historical.js is decommissioned or refactored to read
// daily_team_volume_summary.
export const LEVEL_WEIGHTS = [
  1.0, 0.5, 0.25, 0.125, 0.0625,
  0.03125, 0.015625, 0.0078125, 0.00390625, 0.001953125,
];

// Daily pool payout slots. v4.8.207: Top 50 (was Top 10 through 2026-09-12).
export const POOL_WINNER_COUNT = 50;
export const PAYOUT_TOP50_EFFECTIVE_DATE = '2026-09-13';

// Legacy Top 10 split (40/20/12/4×7). Used only to audit/backfill dates
// before PAYOUT_TOP50_EFFECTIVE_DATE. Do not use for live payouts.
export const PAYOUT_DISTRIBUTION_TOP10_LEGACY = Object.freeze([
  0.40, 0.20, 0.12, 0.04, 0.04, 0.04, 0.04, 0.04, 0.04, 0.04,
]);

// Live daily pool distribution across Top 50 ranks.
// 1st 10%, 2nd 8%, 3rd 6%, 4th–10th 4% each, 11th–20th 2% each,
// 21st–40th 1% each, 41st–50th 0.8% each. Sums to 100%.
export const PAYOUT_DISTRIBUTION = Object.freeze((() => {
  const d = [];
  d.push(0.10, 0.08, 0.06);
  for (let i = 0; i < 7; i++) d.push(0.04);
  for (let i = 0; i < 10; i++) d.push(0.02);
  for (let i = 0; i < 20; i++) d.push(0.01);
  for (let i = 0; i < 10; i++) d.push(0.008);
  return d;
})());

export function payoutDistributionForDate(date) {
  const day = String(date || '').slice(0, 10);
  if (day && day < PAYOUT_TOP50_EFFECTIVE_DATE) return PAYOUT_DISTRIBUTION_TOP10_LEGACY;
  return PAYOUT_DISTRIBUTION;
}

// Fraction of each eligible share-purchase USD that is credited into the
// daily performance pool via trg_pool_ledger_on_purchase.
// v4.8.206: 5% → 10%. Must stay in lock-step with the SQL trigger
// (ROUND(NEW.total_amount * POOL_CONTRIBUTION_RATE, 6)).
export const POOL_CONTRIBUTION_RATE = 0.10;
export const POOL_CONTRIBUTION_PERCENT = 10;

// Payout split: USDT vs share portion for every winner.
// v4.3.76 — Flat 80/20 across all historical tier keys. The tier ladder
// (Prospector -> Tycoon) is retired as a progression system; qualification
// is now a single gate (5 L1 / 5 L2 with >= 1 share each). Legacy users
// whose highest_achieved_category is already 'miner'/'excavator'/
// 'operator'/'tycoon' remain qualified via the non-null gate and receive
// the same 80/20 split as 'prospector' users — no legacy data mutation.
// Keys are preserved so any downstream RANK_SPLITS[category] lookup
// continues to resolve without a code change.
export const RANK_SPLITS = {
  prospector: { usdt: 0.80, share: 0.20 },
  miner:      { usdt: 0.80, share: 0.20 },
  excavator:  { usdt: 0.80, share: 0.20 },
  operator:   { usdt: 0.80, share: 0.20 },
  tycoon:     { usdt: 0.80, share: 0.20 },
};

// Canonical flat split (convenience export). Consumers that do not care
// about the legacy tier key may read this directly.
export const FLAT_PAYOUT_SPLIT = { usdt: 0.80, share: 0.20 };

// Payment-method values that are excluded from competition scoring because
// they represent accounting movements or historical reconciliations rather
// than genuine new-purchase activity.
//
// This list is used in Supabase queries as:
//   .not('payment_method', 'in', `(${EXCLUDED_METHODS.join(',')})`)
//
// All values here must match the exact string values stored in
// aureus_share_purchases.payment_method. Audit trail: confirmed against DB
// on 2026-04-18 — all 6 values below have live rows.
//
// DUAL-HOME PARITY (v4.3.72 / v4.3.78): the same list is hardcoded in TWO
// SQL functions — credit_teams_for_purchase() and trg_pool_ledger_on_purchase()
// — mirrored in database/migrations/gold-diggers-volume-scoring.sql and
// database/migrations/exclude-performance-pool-from-triggers.sql.
// Any change here MUST be mirrored in BOTH SQL functions in the SAME commit.
// Diverging lists caused the 2026-04-16 / 04-17 duplicate-winner incident
// (see D1 audit note in api/cron/process-performance-pool.js).
//
// v4.3.78: 'Performance Pool' and 'Gold Diggers Pool Claim' added to close
// the feedback loop where winner payouts would self-feed the pool (+10% via
// trg_pool_ledger_on_purchase) and inflate next day's leaderboard (+amount
// via credit_teams_for_purchase upline walk).
//
// v4.3.80: 'USDT_COMMISSION_CONVERSION' and 'Commission Shares' added as
// exact matches, plus the LIKE-pattern families 'COMMISSION_LEVEL_%',
// 'COMMISSION_FIX_%', 'COMMISSION_CORRECTION_%' (see EXCLUDED_METHOD_PREFIXES).
// Rationale: the upline commission cascade generates synthetic share
// purchases that represent internal share issuance (not new external
// revenue). Previously these were double-credited — once on the original
// crypto purchase and again on every cascade row generated from it —
// which both inflated the 10% pool and inflated team volume rankings.
// Discovered during 2026-04-18 finalization audit: $982.34 of fake team
// volume and $22.37 of fake pool escrow on a single day.
export const EXCLUDED_METHODS = [
  'Consolidated',
  'Consolidated Remainder',
  'Consolidated Bonus',
  'Legacy Bonus',
  'Received Transfers',
  'Glitch Correction',
  'Performance Pool',
  'Gold Diggers Pool Claim',
  'USDT_COMMISSION_CONVERSION',
  'Commission Shares',
];

// v4.3.80: prefix patterns excluded in addition to the exact-match list above.
// SQL triggers use `payment_method LIKE '<prefix>%'` for each entry; JS
// consumers can use `EXCLUDED_METHOD_PREFIXES.some(p => method.startsWith(p))`.
// Any change here MUST be mirrored in trg_pool_ledger_on_purchase() and
// credit_teams_for_purchase() (see the dual-home parity note above).
export const EXCLUDED_METHOD_PREFIXES = [
  'COMMISSION_LEVEL_',
  'COMMISSION_FIX_',
  'COMMISSION_CORRECTION_',
];

// v4.8.16 — Performance pool leader blocklist.
// Platform admin / treasury accounts that must never lead a team or be
// eligible for performance-pool payouts. Enforced at three surfaces:
//   1. api/teams/register.js              (team creation 403 gate)
//   2. lib/goldDiggersProjection.js       (loadProjection qualified filter)
//   3. api/teams/leaderboard.js           (UI visibility filter)
// Rationale: prior to v4.8.16, admin user 4 (AUREUS) created Team AUREUS
// (id=73) on 2026-05-17 and collected 7 consecutive rank-#1 daily payouts
// totalling $1,099.92 between 2026-05-18 and 2026-05-24. See audit-reports/
// 2026-05-26-pool-payout-cluster-team-73-admin-leader.md.
// IDs in this list cannot register a team, are filtered from pool
// projection, and are hidden from the leaderboard regardless of team
// is_active state. Existing team 73 is also soft-deactivated in DB.
export const POOL_EXCLUDED_LEADER_IDS = Object.freeze([1, 4, 89, 106]);

// ── Daily Performance Pool — Qualified Daily Business (QDB) ────────────
// 15% + 15% + 70% across two distinct legs and the remainder combined.
// A Team Leader's score is the largest amount satisfying those ratios over
// every pair of distinct positive legs (min 3 legs). See lib/goldDiggersQdb.js.
// Effective from the same date as the Top-50 payout expansion.
export const QDB_EFFECTIVE_DATE = '2026-09-13';
export const QDB_LEG_PCT_A = 0.15;
export const QDB_LEG_PCT_B = 0.15;
export const QDB_LEG_PCT_R = 0.70;
